import React, { useState } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Modal, Button, ButtonToolbar, Alert, Spinner, Card, Row, Col, Table, Form } from 'react-bootstrap';
import * as Icon from 'react-feather';

const CANCEL_REASONS = [
    { value: '01', label: '01 - Comprobante emitido con errores con relacion' },
    { value: '02', label: '02 - Comprobante emitido con errores sin relacion' },
    { value: '03', label: '03 - No se llevo a cabo la operacion' },
    { value: '04', label: '04 - Operacion nominativa relacionada con una factura global' },
];

function CartaPorteModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [cancelReason, setCancelReason] = useState('03');
    const [uuidSustitucion, setUuidSustitucion] = useState('');

    const tripId = props.tripId;

    const onModalOpen = () => {
        setError(null);
        setSuccess(null);
        setPreview(null);
        resetCancelForm();
        loadData();
    };

    const resetCancelForm = () => {
        setShowCancelConfirm(false);
        setCancelReason('03');
        setUuidSustitucion('');
    };

    const loadData = async () => {
        setLoading(true);
        try {
            // Load CP status
            const statusRes = await axios.get(`${apiUrl}/cartaporte/status/${tripId}`, {
                headers: { Authorization: authHeader() }
            });
            const statusData = statusRes.data.msg;
            setStatus(statusData);

            // If no CP exists or last one had error/cancelled, load preview
            if (!statusData.exists || statusData.status === 'ERROR' || statusData.status === 'CANCELLED') {
                const previewRes = await axios.get(`${apiUrl}/cartaporte/preview/${tripId}`, {
                    headers: { Authorization: authHeader() }
                });
                setPreview(previewRes.data.msg);
            }
        } catch (err) {
            setError('Error al cargar estado de Carta Porte');
            console.error(err);
        }
        setLoading(false);
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            const res = await axios.post(
                `${apiUrl}/cartaporte/generate/${tripId}`,
                {},
                { headers: { Authorization: authHeader() } }
            );
            setSuccess(`Carta Porte generada exitosamente. UUID: ${res.data.uuid}`);
            await loadData();
        } catch (err) {
            const errData = err.response?.data;
            let errMsg = errData?.error || 'Error al generar Carta Porte';
            if (errData?.details && errData.details !== errMsg) {
                const details = typeof errData.details === 'object' ? JSON.stringify(errData.details) : errData.details;
                errMsg += '\n\nDetalles: ' + details;
            }
            setError(errMsg);
            if (errData?.id) {
                await loadData();
            }
        } finally {
            if (props.setData) props.setData();
            setGenerating(false);
        }
    };

    const handleDownload = async (fileType) => {
        if (!status || !status.id) return;
        try {
            const res = await axios.get(
                `${apiUrl}/cartaporte/download/${status.id}/${fileType}`,
                { headers: { Authorization: authHeader() } }
            );
            const fileData = res.data.msg;
            const byteCharacters = atob(fileData.file);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const mimeType = fileType === 'pdf' ? 'application/pdf' : 'application/xml';
            const blob = new Blob([byteArray], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileData.filename;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(`Error al descargar ${fileType.toUpperCase()}`);
        }
    };

    const handleCancel = async () => {
        if (!status || !status.id) return;

        if (cancelReason === '01' && !uuidSustitucion.trim()) {
            setError('Captura el UUID de sustitucion para el motivo 01.');
            return;
        }

        setCanceling(true);
        setError(null);
        try {
            await axios.post(
                `${apiUrl}/cartaporte/cancel/${status.id}`,
                {
                    motivo: cancelReason,
                    uuid_sustitucion: uuidSustitucion.trim() || null,
                },
                { headers: { Authorization: authHeader() } }
            );
            setSuccess('Carta Porte cancelada exitosamente.');
            resetCancelForm();
            await loadData();
            if (props.setData) props.setData();
        } catch (err) {
            const errData = err.response?.data;
            const errMsg = errData?.details || errData?.error || 'Error al cancelar Carta Porte';
            setError(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
        } finally {
            setCanceling(false);
        }
    };

    const renderCancelPanel = () => showCancelConfirm && (
        <Alert variant="warning" className="mt-3 mb-0">
            <Alert.Heading className="h5">Cancelar Carta Porte</Alert.Heading>
            <p className="mb-3">Esta seguro que desea cancelar la Carta Porte?</p>
            <Form.Group className="mb-3">
                <Form.Label>Motivo de cancelacion</Form.Label>
                <Form.Select
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                >
                    {CANCEL_REASONS.map((reason) => (
                        <option key={reason.value} value={reason.value}>
                            {reason.label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>
            {cancelReason === '01' && (
                <Form.Group className="mb-3">
                    <Form.Label>UUID de sustitucion</Form.Label>
                    <Form.Control
                        value={uuidSustitucion}
                        onChange={(e) => setUuidSustitucion(e.target.value)}
                        placeholder="UUID del CFDI que sustituye"
                    />
                </Form.Group>
            )}
            <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={() => setShowCancelConfirm(false)} disabled={canceling}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={handleCancel}
                    disabled={canceling || (cancelReason === '01' && !uuidSustitucion.trim())}
                >
                    {canceling ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Cancelando...
                        </>
                    ) : (
                        'Si'
                    )}
                </Button>
            </div>
        </Alert>
    );

    const renderStampedView = () => (
        <div>
            <Alert variant="success">
                <Alert.Heading>Carta Porte Timbrada</Alert.Heading>
                <p><strong>UUID:</strong> {status.uuid}</p>
                <p><strong>Folio:</strong> {status.folio || 'N/A'}</p>
                <p><strong>Fecha de Timbrado:</strong> {status.fecha_timbrado || 'N/A'}</p>
                <p><strong>Distancia Total:</strong> {status.total_dist_rec} km</p>
            </Alert>
            <div className="d-flex gap-2">
                <Button variant="outline-primary" onClick={() => handleDownload('xml')}>
                    <Icon.Download size={14} /> Descargar XML
                </Button>
                <Button variant="outline-danger" onClick={() => handleDownload('pdf')}>
                    <Icon.Download size={14} /> Descargar PDF
                </Button>
                <Button variant="danger" onClick={() => setShowCancelConfirm(true)}>
                    <Icon.XCircle size={14} /> Cancelar
                </Button>
            </div>
            {renderCancelPanel()}
        </div>
    );

    const renderPreviewView = () => {
        if (!preview) return null;
        const { validation, emisor, operador, unidad, remolques = [], paradas, mercancias } = preview;

        return (
            <div>
                {/* Validation errors */}
                {validation && !validation.valid && (
                    <Alert variant="danger">
                        <Alert.Heading>Campos Faltantes</Alert.Heading>
                        <ul className="mb-0">
                            {validation.errors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {validation && validation.warnings && validation.warnings.length > 0 && (
                    <Alert variant="warning">
                        <Alert.Heading>Advertencias</Alert.Heading>
                        <ul className="mb-0">
                            {validation.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Summary cards - always show */}
                {/* Emisor */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Emisor</h6></Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}><strong>RFC:</strong> {emisor.rfc || '-'}</Col>
                            <Col md={3}><strong>Nombre:</strong> {emisor.nombre || '-'}</Col>
                            <Col md={3}><strong>Regimen:</strong> {emisor.regimen_fiscal || '-'}</Col>
                            <Col md={3}><strong>C.P.:</strong> {emisor.codigo_postal || '-'}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Operador */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Operador</h6></Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4}><strong>Nombre:</strong> {operador.nombre || '-'}</Col>
                            <Col md={4}><strong>RFC:</strong> {operador.rfc || '-'}</Col>
                            <Col md={4}><strong>No. Licencia:</strong> {operador.num_licencia || '-'}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Unidad */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Unidad</h6></Card.Header>
                    <Card.Body>
                        <Row className="mb-2">
                            <Col md={3}><strong>Nombre:</strong> {unidad.nombre || '-'}</Col>
                            <Col md={3}><strong>Placas:</strong> {unidad.placas || '-'}</Col>
                            <Col md={3}><strong>Config:</strong> {unidad.config_vehicular || '-'}</Col>
                            <Col md={3}><strong>Peso Bruto:</strong> {unidad.peso_bruto_vehicular ? `${unidad.peso_bruto_vehicular} Ton` : '-'}</Col>
                        </Row>
                        <Row>
                            <Col md={3}><strong>Permiso SCT:</strong> {unidad.permiso_sct || '-'}</Col>
                            <Col md={3}><strong>No. Permiso:</strong> {unidad.num_permiso_sct || '-'}</Col>
                            <Col md={3}><strong>Aseg. Resp Civil:</strong> {unidad.aseguradora_resp_civil || '-'}</Col>
                            <Col md={3}><strong>Poliza:</strong> {unidad.poliza_resp_civil || '-'}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Remolques */}
                {remolques.length > 0 && (
                    <Card className="mb-3">
                        <Card.Header><h6 className="mb-0">Remolques ({remolques.length})</h6></Card.Header>
                        <Card.Body>
                            <Table size="sm" bordered>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Nombre</th>
                                        <th>Placas</th>
                                        <th>Subtipo SAT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {remolques.map((remolque, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{remolque.nombre || '-'}</td>
                                            <td>{remolque.placas || '-'}</td>
                                            <td>{remolque.subtipo_rem || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                )}

                {/* Paradas */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Paradas ({paradas.length})</h6></Card.Header>
                    <Card.Body>
                        {paradas.length > 0 ? (
                            <Table size="sm" bordered>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Origen</th>
                                        <th>C.P. Origen</th>
                                        <th>RFC Origen</th>
                                        <th>Destino</th>
                                        <th>C.P. Destino</th>
                                        <th>RFC Destino</th>
                                        <th>Distancia (km)</th>
                                        <th>Peso (ton)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paradas.map((p, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{p.origin_address || '-'}</td>
                                            <td>{p.origin_postal_code || '-'}</td>
                                            <td>{p.origin_rfc || '-'}</td>
                                            <td>{p.destination_address || '-'}</td>
                                            <td>{p.destination_postal_code || '-'}</td>
                                            <td>{p.destination_rfc || '-'}</td>
                                            <td>{p.distance_km || '-'}</td>
                                            <td>{p.weight || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted mb-0">Sin paradas</p>
                        )}
                    </Card.Body>
                </Card>

                {/* Mercancias */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Mercancias ({mercancias.length})</h6></Card.Header>
                    <Card.Body>
                        {mercancias.length > 0 ? (
                            <Table size="sm" bordered>
                                <thead>
                                    <tr>
                                        <th>Clave SAT</th>
                                        <th>Descripcion</th>
                                        <th>Cantidad</th>
                                        <th>Unidad</th>
                                        <th>Peso (kg)</th>
                                        <th>Valor</th>
                                        <th>Moneda</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mercancias.map((m, i) => (
                                        <tr key={i}>
                                            <td>{m.bienes_transp || '-'}</td>
                                            <td>{m.descripcion || '-'}</td>
                                            <td>{m.cantidad || '-'}</td>
                                            <td>{m.clave_unidad || '-'}</td>
                                            <td>{m.peso_en_kg || '-'}</td>
                                            <td>{m.valor_mercancia || '-'}</td>
                                            <td>{m.moneda || 'MXN'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <Alert variant="warning" className="mb-0">
                                No hay mercancias registradas. Agrega mercancias desde Editar Viaje.
                            </Alert>
                        )}
                    </Card.Body>
                </Card>

                {/* Generate button */}
                {validation && validation.valid && mercancias.length > 0 && (
                    <div className="text-end">
                        <Button
                            variant="primary"
                            onClick={handleGenerate}
                            disabled={generating}
                        >
                            {generating ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Generando...
                                </>
                            ) : (
                                <>
                                    <Icon.FileText size={14} className="me-1" />
                                    Generar Carta Porte
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            show={props.show}
            onHide={props.onHide}
            onShow={onModalOpen}
            size="xl"
            aria-labelledby="carta-porte-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title id="carta-porte-modal">
                    Carta Porte - Viaje #{tripId}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Cargando...</p>
                    </div>
                ) : (
                    <>
                        {error && <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ whiteSpace: 'pre-wrap' }}>{error}</Alert>}
                        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

                        {status && status.exists && status.status === 'STAMPED' && renderStampedView()}
                        {status && status.exists && status.status === 'ERROR' && (
                            <>
                                <Alert variant="danger">
                                    <Alert.Heading>Error en Generacion Anterior</Alert.Heading>
                                    <p>{status.error_message}</p>
                                </Alert>
                                {renderPreviewView()}
                            </>
                        )}
                        {status && status.exists && status.status === 'CANCELLED' && (
                            <>
                                <Alert variant="secondary">
                                    Esta Carta Porte fue cancelada el {status.cancel_date || 'N/A'}.
                                    {status.cancel_reason && (
                                        <div><strong>Motivo:</strong> {status.cancel_reason}</div>
                                    )}
                                </Alert>
                                {renderPreviewView()}
                            </>
                        )}
                        {(!status || !status.exists) && renderPreviewView()}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}


function CartaPorteButton({ tripId, cartaPorteStatus, setData }) {
    const [modalShow, setModalShow] = useState(false);

    const getButtonState = () => {
        if (cartaPorteStatus?.status === 'STAMPED') {
            return {
                variant: 'outline-success',
                title: `Carta Porte timbrada${cartaPorteStatus.uuid ? ` - UUID: ${cartaPorteStatus.uuid}` : ''}`,
            };
        }

        if (cartaPorteStatus?.status === 'ERROR') {
            return {
                variant: 'outline-danger',
                title: 'Carta Porte con error. Abre para revisar e intentar de nuevo.',
            };
        }

        return {
            variant: 'outline-secondary',
            title: 'Carta Porte',
        };
    };

    const handleClose = () => {
        if (setData) setData();
        setModalShow(false);
    };

    const buttonState = getButtonState();

    return (
        <ButtonToolbar>
            <Button
                size="sm"
                variant={buttonState.variant}
                style={{ padding: '0 0 0 0' }}
                onClick={() => setModalShow(true)}
                title={buttonState.title}
                aria-label={buttonState.title}
            >
                <Icon.FileText size={16} />
            </Button>
            <CartaPorteModal
                show={modalShow}
                onHide={handleClose}
                tripId={tripId}
                setData={setData}
            />
        </ButtonToolbar>
    );
}

export default CartaPorteButton;
